import Docxtemplater from 'docxtemplater';
import ImageModule from 'docxtemplater-image-module-free';
import { saveAs } from 'file-saver';
import PizZip from 'pizzip';

import {
  buildWordExportFileName,
  MAX_WORD_EXPORT_IMAGES,
} from './model/wordExport';
import {
  replaceTableMarkersInWordXml,
  splitLineByTableTokens,
} from './model/wordTableExport';

const base64DataURLToArrayBuffer = (dataURL) => {
  const base64Regex = /^data:image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
  if (!base64Regex.test(dataURL)) {
    return false;
  }

  const stringBase64 = dataURL.replace(base64Regex, '');
  const binaryString = window.atob(stringBase64);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index++) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return bytes.buffer;
};

const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index++) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return bytes.buffer;
};

export const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);

  for (let index = 0; index < bytes.byteLength; index++) {
    binary += String.fromCharCode(bytes[index]);
  }

  return window.btoa(binary);
};

const createImageModuleOptions = (images) => ({
  centered: false,
  getImage: (tagValue) => base64DataURLToArrayBuffer(tagValue),
  getSize: (image, tagValue) => {
    const foundImage = images.find(({ url }) => url === tagValue);
    if (foundImage?.width && foundImage?.height) {
      const targetWidth = 600;
      const ratio = foundImage.height / foundImage.width;
      return [targetWidth, Math.round(targetWidth * ratio)];
    }

    return [600, 400];
  },
});

const createHtmlToWordLines = (tableMarkers) => (htmlText) => {
  const temporaryElement = document.createElement('div');
  temporaryElement.innerHTML = String(htmlText ?? '').replace(
    /<br\s*\/?>/gi,
    '\n'
  );
  const plainText =
    temporaryElement.textContent || temporaryElement.innerText || '';

  return plainText
    .split('\n')
    .flatMap((line) => splitLineByTableTokens(line, tableMarkers))
    .map((line) => line.trimEnd())
    .filter((line) => line.trim() !== '');
};

const createImagesData = (images) => {
  const imagesData = {};

  for (let index = 0; index < MAX_WORD_EXPORT_IMAGES; index++) {
    imagesData[`image${index + 1}`] = images[index]
      ? images[index].url
      : null;
    imagesData[`caption${index + 1}`] = images[index]
      ? images[index].caption
      : null;
  }

  return imagesData;
};

/**
 * Формирует и скачивает Word-документ из текущего состояния рабочего шаблона.
 *
 * Функция намеренно сохраняет существующие правила преобразования текста,
 * таблиц, снимков и рекомендаций.
 */
export const downloadWordDocument = async ({
  templateBase64,
  blocks,
  images,
  recommendationsText,
  patientName,
  patientBirthdate,
  patientGender,
  studyNumber,
  studyDate,
  studyType,
  studyAlias,
  contrastType,
  studyNote,
  complaints,
}) => {
  const templateBuffer = base64ToArrayBuffer(templateBase64);
  const templateZip = new PizZip(templateBuffer);
  const imageModule = new ImageModule(createImageModuleOptions(images));
  const documentTemplate = new Docxtemplater(templateZip, {
    modules: [imageModule],
    paragraphLoop: true,
    linebreaks: true,
  });

  const tableMarkers = [];
  const htmlToWordLines = createHtmlToWordLines(tableMarkers);
  const zones = blocks
    .filter(({ zone_name: zoneName }) => !zoneName.endsWith('_conclusion'))
    .map((block) => ({
      zone: block.zone_name,
      texts: block.instances.flatMap(({ text }) => htmlToWordLines(text)),
    }));

  const conclusionBlock = blocks.find(({ zone_name: zoneName }) =>
    zoneName.endsWith('_conclusion')
  );
  const conclusionZoneDescription = conclusionBlock
    ? conclusionBlock.zone_description.toUpperCase()
    : '';
  const conclusionText = conclusionBlock
    ? conclusionBlock.instances.flatMap(({ text }) => htmlToWordLines(text))
    : [];

  console.log('image1 url:', images[0]?.url);

  const patientNameExport = patientName.trim();
  const patientBirthdateExport = patientBirthdate.trim();
  const studyNoteValue = studyNote.trim();
  const studyNoteExport = studyNoteValue ? `(${studyNoteValue})` : '';
  const recommendationsTextExport = recommendationsText.trim()
    ? recommendationsText.replace(/<br\s*\/?>/gi, '\n').split('\n')
    : [];

  documentTemplate.render({
    zones,
    ...createImagesData(images),
    conclusionZoneDescription,
    conclusionText,
    recommendationsZoneDescription: 'РЕКОМЕНДАЦИИ',
    recommendationsText: recommendationsTextExport,
    patientNameExport,
    patientBirthdateExport,
    patientGenderExport: patientGender,
    studyNumberExport: studyNumber,
    studyDateExport: studyDate,
    studyTypeExport: studyType,
    contrastTypeExport: contrastType,
    studyNoteExport,
    complaintsExport: complaints,
  });

  if (tableMarkers.length > 0) {
    const documentXmlFile = documentTemplate
      .getZip()
      .file('word/document.xml');

    if (documentXmlFile) {
      const documentXml = documentXmlFile.asText();
      const updatedDocumentXml = replaceTableMarkersInWordXml(
        documentXml,
        tableMarkers
      );
      documentTemplate
        .getZip()
        .file('word/document.xml', updatedDocumentXml);
    }
  }

  const documentBlob = documentTemplate.getZip().generate({
    type: 'blob',
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const fileName = buildWordExportFileName({
    patientName: patientNameExport,
    studyAlias,
    contrastType,
    studyDate,
  });
  saveAs(documentBlob, fileName);
};
